/**
 * Test hooks for Phase J Playwright specs.
 *
 * Centralized window-attached hooks that let drafted e2e specs in
 * `packages/repancy/e2e/*.spec.ts` drive surfaces that aren't
 * trivially reachable from a real-user click path (NIPE proposal
 * injection, ConflictDialog force-mount, outbound-queue inspection).
 *
 * **Gated:** hooks install ONLY when `?test=1` is in the URL.
 * Vitest's default `MODE='test'` is intentionally NOT a trigger —
 * vitest unit tests shouldn't install window hooks unless they
 * explicitly opt in by writing to `window.location.search`. Playwright
 * specs invoke the hooks by navigating to `/?test=1`.
 *
 * Production builds (no query param) skip installation entirely. The
 * module is imported unconditionally — the install function self-gates.
 *
 * Keep this module narrow:
 *   - One install() per hook
 *   - Each hook returns minimal data (no full Convex rows leaking)
 *   - No write paths that could be triggered accidentally without the
 *     query param guard
 *
 * @see packages/repancy/e2e/*.spec.ts — the consumers of these hooks
 * @see packages/repancy/docs/testing/playwright-strategy.md — strategy
 */

import type { ConvexReactClient } from 'convex/react';

import {
  bodyPreview,
  clearPendingWriteBody,
  getAutoApproveAttribution,
  getPendingWriteBody,
  hashBody,
  listCachedPendingWriteIds,
  setAutoApproveAttribution,
  setPendingWriteBody,
} from '../nipe/pendingWriteBodies';
import {
  getFakeWrites,
  installFakeDirectoryPicker,
  resetFakeWrites,
  uninstallFakeDirectoryPicker,
} from './fakeDirectoryPicker';

/**
 * Returns true if the current page should install test hooks.
 *
 * Gate: ONLY `?test=1` (or `?test=1&...`) in the URL. Vitest's default
 * MODE='test' is NOT a trigger — vitest unit tests shouldn't install
 * window hooks unless they explicitly opt in by writing to
 * `window.location.search`. Playwright specs invoke the hooks by
 * navigating to `/?test=1`.
 *
 * Cheap; called once at module init time + each time before reading
 * hook state.
 */
function shouldInstall(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('test') === '1';
  } catch {
    /* SSR or weird sandbox — skip */
    return false;
  }
}

interface TestHookWindow {
  /**
   * Force-mount the ConflictDialog with the given props. The consumer
   * mounts the component via a portal-attached root managed by
   * DesktopShell when present; if no DesktopShell test-mount slot is
   * registered, the hook logs a warning and no-ops.
   */
  __repancyTestForceConflictDialog?: (opts: {
    documentTitle: string;
    remoteAuthor?: string;
    onResolve?: (resolution: string) => void;
    onCancel?: () => void;
  }) => void;

  /**
   * Inject a synthetic pending-write row for the NIPE tray tests.
   * Returns the synthetic row id. Tests can then inspect the tray
   * UI without firing a real NIPE chat round-trip.
   */
  __repancyTestInjectPendingWrite?: (row: {
    summary: string;
    folderPath: string;
    fileName: string;
  }) => string;

  /**
   * Inject a NIPE proposal that goes through the full pipeline
   * (proposeSharePointDocument tool → Convex propose → auto-approve
   * check). Returns the pendingWriteId so the test can poll status.
   *
   * NOT YET IMPLEMENTED — placeholder. Needs the SPA to wire its
   * useNipeChat ToolRunnerContext to a known-window-attached factory
   * that this hook can invoke. Tracked in CURRENT-BACKLOG.md.
   */
  __repancyTestInjectNipeProposal?: (input: {
    folderPath: string;
    fileName: string;
    body: string;
    summary: string;
  }) => Promise<string>;

  /**
   * Read the current status of a pending-write row.
   * NOT YET IMPLEMENTED — needs Convex client exposed for the read.
   */
  __repancyTestGetPendingWriteStatus?: (id: string) => Promise<string>;

  /**
   * Read the most recent SP audit row for a given pendingWriteId.
   * Used to confirm B.6 autoApprovedByRuleId attribution lands.
   * NOT YET IMPLEMENTED — needs Convex client exposed.
   */
  __repancyTestGetAuditRowForWrite?: (id: string) => Promise<{
    autoApprovedByRuleId?: string;
    autoApprovedByRuleLabel?: string;
  } | null>;

  /**
   * Read a pending-write row's persisted shape (for B.5 nipe_ai_
   * filename verification).
   * NOT YET IMPLEMENTED.
   */
  __repancyTestGetPendingWriteRow?: (id: string) => Promise<{
    fileName: string;
    bodyPreview?: string;
  } | null>;

  /**
   * Enqueue a row into the vault outbound queue.
   * NOT YET IMPLEMENTED — needs Convex client + the existing
   * `vaultOutboundQueue.enqueue` mutation reachable from window.
   */
  __repancyTestEnqueueOutbound?: (input: {
    notePath: string;
    body: string;
  }) => Promise<string>;

  /**
   * Force a drain of the outbound queue. Bypasses the production
   * tick cadence so tests don't have to wait.
   * NOT YET IMPLEMENTED — needs drain worker exposed.
   */
  __repancyTestDrainOutbound?: () => Promise<void>;

  /**
   * Read the current status of an outbound-queue row.
   * NOT YET IMPLEMENTED — needs Convex client.
   */
  __repancyTestGetOutboundStatus?: (id: string) => Promise<string>;

  /**
   * Legacy marker — kept as a stable alias for the pre-Phase-D demo-bootstrap
   * flag. New specs should poll `__repancyAppReady` (set at the same moment
   * + survives the post-Phase-D demo-mode collapse without requiring a
   * Win95Desktop mount). `markDemoReady()` flips both flags atomically.
   */
  __repancyDemoReady?: boolean;

  /**
   * Phase I marker — set true once the SPA has installed test hooks. Unlike
   * `__repancyDemoReady` (which historically waited on useDemoBootstrap +
   * post-Phase-D waits on useUserBootstrap, requiring Win95Desktop mount),
   * this flag flips during `installTestHooks()` so unauthed `/?test=1`
   * surfaces (e.g. sign-in page) don't hang e2e waits forever. Both flags
   * are flipped by `markDemoReady()` to preserve the legacy contract.
   */
  __repancyAppReady?: boolean;

  /**
   * F-OUTBOUND-SAVEBACK-HOOK — install a test-only saveBack that the
   * `__repancyTestDrainOutbound` hook will pass to `drainOutbound`
   * instead of `null`. Lets J.12's 412 path (test 2) drive the
   * needs-resolution branch without staging a real Graph PUT.
   *
   * The injected fn is module-scope; only one is active at a time.
   * Pass `null` to clear.
   */
  __repancyTestInstallSaveBack?: (
    fn:
      | ((input: { notePath: string; body: string; baseEtag: string | null }) => Promise<{
          status: 'ok' | 'conflict' | 'error';
          newEtag?: string;
          remoteEtag?: string;
          errorMessage?: string;
        }>)
      | null,
  ) => void;

  /**
   * F-FAKE-DIR-PICKER — replace `window.showDirectoryPicker` with an
   * in-memory FakeDirectoryHandle so the J vault-export-folder spec
   * can run in headless Chromium (real FSAP isn't exposed without a
   * Chrome flag). Captures each `createWritable().close()` call's
   * full path into a module-scope `writes` array.
   */
  __repancyTestInstallFakeDirectoryPicker?: () => void;

  /**
   * Read back the FakeDirectoryHandle write list — call after running
   * an export to assert files made it through.
   */
  __repancyTestGetFakeWrites?: () => readonly string[];

  /**
   * D.3.5 — set to true by NipePortableSiteDropDialog when its
   * post-MSAL-redirect resume effect has reconstituted state from the
   * `repancy:vault-drop-provision-resume` sessionStorage stash.
   *
   * Headless smoke + the future `repancy-cli smoke vault-drop
   * --print-resume-event` flag both consume this hook to confirm the
   * round-trip completed before asserting on the panel's behavior.
   */
  __repancyTestVaultDropResumeFired?: boolean;

  /**
   * D.3.5 — set to true by VaultDropProvisionPanel the first time its
   * `startProvision` runs, regardless of outcome. Distinguishes "user
   * clicked Start" / "resume auto-fired Start" from "panel mounted but
   * never started."
   *
   * Maps to `repancy-cli smoke vault-drop --print-start-event` exit
   * signal.
   */
  __repancyTestVaultDropProvisionStartFired?: boolean;

  /**
   * D.3.5 — terminal job status as observed by the panel. One of
   * 'complete' | 'failed' | null. Stays null while the job is
   * still queued/claimed/running, and is set exactly once when the
   * Convex `getVaultExportJob` query first returns a terminal status.
   *
   * Maps to the exit code of `repancy-cli smoke vault-drop`
   * (0 = complete, non-zero = failed).
   */
  __repancyTestVaultDropFinalStatus?: 'complete' | 'failed' | null;
}

/**
 * In-memory store for test-injected pending writes. The NIPE tray
 * reads from Convex in production; for tests we register a parallel
 * synthetic list that the tray component can render alongside.
 *
 * Subscribers (the tray component) listen for changes via
 * `subscribeTestPendingWrites`.
 */
type TestPendingWrite = {
  _id: string;
  summary: string;
  folderPath: string;
  fileName: string;
  bodyPreview: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'failed';
  createdAt: string;
};

const testPendingWrites: TestPendingWrite[] = [];
const testPendingListeners = new Set<(rows: TestPendingWrite[]) => void>();

function notifyTestPendingListeners(): void {
  const snapshot = [...testPendingWrites];
  for (const fn of testPendingListeners) {
    try {
      fn(snapshot);
    } catch {
      /* listener error — ignore so one bad subscriber can't poison others */
    }
  }
}

/**
 * The tray component can call this to subscribe to test-injected
 * pending writes. Returns an unsubscribe function. Always returns the
 * current synthetic list (empty when no hooks are installed).
 */
export function subscribeTestPendingWrites(
  listener: (rows: TestPendingWrite[]) => void,
): () => void {
  testPendingListeners.add(listener);
  listener([...testPendingWrites]);
  return () => {
    testPendingListeners.delete(listener);
  };
}

/**
 * Read the current snapshot of test-injected pending writes
 * (no subscription). Always returns a copy.
 */
export function getTestPendingWritesSnapshot(): ReadonlyArray<TestPendingWrite> {
  return [...testPendingWrites];
}

// Module-scoped Convex client reference. `installConvexClientForTests`
// stores it so the queue/audit hooks can mutate/query without the
// caller passing a client every time. ?test=1 gates all reads.
let convexClientForTests: ConvexReactClient | null = null;

// F-OUTBOUND-SAVEBACK-HOOK — see TestHookWindow.__repancyTestInstallSaveBack
// docstring. Module-scoped so the drain hook can read it without the
// caller threading the fn through every call.
type InjectedSaveBackFn = (input: {
  notePath: string;
  body: string;
  baseEtag: string | null;
}) => Promise<{
  status: 'ok' | 'conflict' | 'error';
  newEtag?: string;
  remoteEtag?: string;
  errorMessage?: string;
}>;
let injectedSaveBackForTests: InjectedSaveBackFn | null = null;

/**
 * Stash a Convex client reference for the test hooks to use. Called
 * once at app bootstrap from main.tsx (right after the client is
 * constructed). Self-gated — no-op outside test mode.
 */
export function installConvexClientForTests(
  client: ConvexReactClient,
): void {
  if (!shouldInstall()) return;
  convexClientForTests = client;
}

/**
 * Mark the SPA's bootstrap as complete. Historically called from
 * useDemoBootstrap when seedForUser + seedDemoSchemaProfiles both
 * resolved (Phase J contract); post-Phase-D it's called from
 * useUserBootstrap once Clerk + Convex resolve, AND eagerly from
 * `installTestHooks` so unauthed `/?test=1` surfaces (the sign-up
 * surface at `/`, `/sign-in` before redirect) don't hang specs that
 * poll on the flag.
 *
 * Flips BOTH `__repancyDemoReady` (legacy) and `__repancyAppReady`
 * (Phase I) so old specs keep working while new code prefers the
 * non-demo-anchored name.
 */
export function markDemoReady(): void {
  if (!shouldInstall()) return;
  const w = window as unknown as {
    __repancyDemoReady?: boolean;
    __repancyAppReady?: boolean;
  };
  w.__repancyDemoReady = true;
  w.__repancyAppReady = true;
}

/**
 * D.3.5 — mark vault-drop post-MSAL-redirect resume as fired. Called
 * by NipePortableSiteDropDialog once it reconstitutes its dialog state
 * from the sessionStorage stash. Self-gated.
 */
export function markVaultDropResumeFired(): void {
  if (!shouldInstall()) return;
  (window as unknown as TestHookWindow).__repancyTestVaultDropResumeFired = true;
}

/**
 * D.3.5 — mark vault-drop `startProvision` as having run at least once.
 * Called by VaultDropProvisionPanel from inside startProvision before
 * any await. Self-gated.
 */
export function markVaultDropProvisionStartFired(): void {
  if (!shouldInstall()) return;
  (window as unknown as TestHookWindow).__repancyTestVaultDropProvisionStartFired = true;
}

/**
 * D.3.5 — record the terminal status of a vault-drop provisioning job.
 * Called once by VaultDropProvisionPanel when the Convex query first
 * returns 'complete' or 'failed'. Self-gated.
 */
export function setVaultDropFinalStatus(status: 'complete' | 'failed'): void {
  if (!shouldInstall()) return;
  (window as unknown as TestHookWindow).__repancyTestVaultDropFinalStatus = status;
}

/**
 * Install hooks on window. Self-gated — no-op outside test mode.
 * Re-running is safe (overwrites previous handlers).
 *
 * Call this once during app bootstrap (`main.tsx` import).
 */
export function installTestHooks(): void {
  if (!shouldInstall()) return;

  // Phase I — flip the ready flags eagerly. Unauthed `/?test=1` surfaces
  // (sign-in card before redirect) used to hang specs that polled
  // `__repancyDemoReady` because `useUserBootstrap`'s post-bootstrap
  // markDemoReady() only fires once Win95Desktop mounts. We re-flip
  // from `useUserBootstrap` after Clerk + Convex resolve so authenticated
  // contexts get a fresh signal too; this eager flip simply ensures any
  // intermediate poll succeeds.
  markDemoReady();

  const w = window as unknown as TestHookWindow;

  // ConflictDialog force-mount — actual mount surface needs a
  // listener inside DesktopShell. Until that lands, store the call
  // for later inspection by the spec (so the spec can assert the
  // hook was reachable even if no dialog appeared).
  w.__repancyTestForceConflictDialog = (opts) => {
    // Defer to a global event so DesktopShell can subscribe.
    window.dispatchEvent(
      new CustomEvent('repancy:test-force-conflict-dialog', {
        detail: opts,
      }),
    );
  };

  // Pending-write injection. Adds to the in-memory store + notifies
  // subscribed tray components.
  w.__repancyTestInjectPendingWrite = (row) => {
    const id = `test-write-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    testPendingWrites.push({
      _id: id,
      summary: row.summary,
      folderPath: row.folderPath,
      fileName: row.fileName,
      bodyPreview: row.summary.slice(0, 200),
      status: 'pending',
      createdAt: new Date().toISOString(),
    });
    notifyTestPendingListeners();
    return id;
  };

  // ── Convex-backed hooks (require installConvexClientForTests) ──
  //
  // These hooks fire real Convex queries/mutations against the test
  // deploy (industrious-ibex-275). They throw if the Convex client
  // isn't yet installed — Playwright specs should wait on
  // window.__repancyDemoReady before invoking them.

  function requireConvex(): ConvexReactClient {
    if (!convexClientForTests) {
      throw new Error(
        'Convex client not installed for tests — installConvexClientForTests not yet called',
      );
    }
    return convexClientForTests;
  }

  // Lazy api import to keep the type surface narrow; the api object is
  // a Proxy that doesn't survive String() but is fine to pass to
  // mutation()/query() at runtime.
  type ConvexApiShape = {
    nipePendingWrites: {
      propose: unknown;
      decide: unknown;
      listForSlug: unknown;
    };
    nipeSharePointAuditLog: {
      listRecent?: unknown;
    };
    vaultOutboundQueue: {
      enqueue: unknown;
      claimNext: unknown;
      markCompleted: unknown;
      markFailed: unknown;
      listForSlug: unknown;
    };
  };

  function loadConvexApi(): ConvexApiShape | null {
    // The hooks below import api lazily via the global registration
    // that main.tsx performs (window.__repancyConvexApi). main.tsx
    // sets this same-tick after constructing the client.
    const reg = (window as unknown as { __repancyConvexApi?: ConvexApiShape })
      .__repancyConvexApi;
    return reg ?? null;
  }

  /**
   * Inject a NIPE proposal through the real Convex pipeline. Runs
   * propose() -> stores body in pendingWriteBodies -> returns the row
   * id. Auto-approve is NOT triggered by this hook (the SPA's
   * useNipeChat handles that on real tool calls); tests that need
   * auto-approve to fire should call decide({approved}) themselves.
   */
  w.__repancyTestInjectNipeProposal = async (input) => {
    const convex = requireConvex();
    const api = loadConvexApi();
    if (!api) {
      throw new Error('__repancyConvexApi not registered — wire main.tsx');
    }
    // Apply the B.5 NIPE-authored markers so this hook matches the
    // production `proposeSharePointDocument` tool path (`markNipeAuthored`
    // is normally invoked there; the test hook bypasses that tool and
    // mutates Convex directly, so we apply the marker here).
    const { markNipeAuthored } = await import('../nipe/markNipeAuthored');
    const marked = markNipeAuthored({
      fileName: input.fileName,
      body: input.body,
    });
    const hash = await hashBody(marked.body);
    const slug =
      new URLSearchParams(window.location.search).get('clientSlug') ?? 'test';
    const id = (await convex.mutation(api.nipePendingWrites.propose as never, {
      clientSlug: slug,
      kind: 'sp-create-doc',
      folderPath: input.folderPath,
      fileName: marked.fileName,
      bodyHash: hash,
      bodyPreview: bodyPreview(marked.body),
      summary: input.summary,
    } as never)) as string;
    setPendingWriteBody(id, marked.body);
    return id;
  };

  /**
   * Read the current status of a pending-write row. Tries the live
   * tables first; reaches into the synthetic store as a fallback.
   */
  w.__repancyTestGetPendingWriteStatus = async (id) => {
    // Synthetic rows (from __repancyTestInjectPendingWrite) live only
    // in-memory.
    const synthetic = testPendingWrites.find((r) => r._id === id);
    if (synthetic) return synthetic.status;
    const convex = requireConvex();
    const api = loadConvexApi();
    if (!api) throw new Error('__repancyConvexApi not registered');
    // Read via listForUser then filter by id. listForUser is the
    // existing public query; we don't add a getById specifically for
    // tests to keep the read surface narrow.
    const slug2 =
      new URLSearchParams(window.location.search).get('clientSlug') ?? 'test';
    const rows = (await convex.query(
      api.nipePendingWrites.listForSlug as never,
      { clientSlug: slug2 } as never,
    )) as Array<{ _id: string; status: string }>;
    return rows.find((r) => r._id === id)?.status ?? 'unknown';
  };

  /**
   * Read the most recent SP audit row for a pendingWriteId. Used to
   * confirm B.6 autoApprovedByRuleId attribution lands. Requires
   * api.nipeSharePointAuditLog.listRecent (added in this commit if
   * not yet present).
   */
  w.__repancyTestGetAuditRowForWrite = async (id) => {
    const convex = requireConvex();
    const api = loadConvexApi();
    if (!api?.nipeSharePointAuditLog?.listRecent) {
      // No listRecent exported yet — return null so the spec can
      // fixme this assertion without crashing.
      return null;
    }
    const rows = (await convex.query(
      api.nipeSharePointAuditLog.listRecent as never,
      { limit: 50 } as never,
    )) as Array<{
      args: string;
      autoApprovedByRuleId?: string;
      autoApprovedByRuleLabel?: string;
    }>;
    // The audit row's args is JSON containing folderPath + fileName.
    // We can't match by pendingWriteId directly (audit doesn't store
    // it). Heuristic: return the most recent row that mentions the
    // id; fall back to most recent overall.
    const match = rows.find((r) => r.args.includes(id));
    return (
      match ?? rows[0] ?? null
    ) as unknown as {
      autoApprovedByRuleId?: string;
      autoApprovedByRuleLabel?: string;
    } | null;
  };

  /**
   * Read the persisted shape of a pending-write row (filename + body
   * preview). Used for B.5 nipe_ai_ prefix verification.
   */
  w.__repancyTestGetPendingWriteRow = async (id) => {
    const convex = requireConvex();
    const api = loadConvexApi();
    if (!api) throw new Error('__repancyConvexApi not registered');
    const slug =
      new URLSearchParams(window.location.search).get('clientSlug') ?? 'test';
    const rows = (await convex.query(api.nipePendingWrites.listForSlug as never, {
      clientSlug: slug,
    } as never)) as Array<{ _id: string; fileName: string; bodyPreview?: string }>;
    const match = rows.find((r) => r._id === id);
    if (!match) return null;
    return { fileName: match.fileName, bodyPreview: match.bodyPreview };
  };

  /** Enqueue a row into the vault outbound queue (E.2 path). */
  w.__repancyTestEnqueueOutbound = async (input) => {
    const convex = requireConvex();
    const api = loadConvexApi();
    if (!api) throw new Error('__repancyConvexApi not registered');
    const slug =
      new URLSearchParams(window.location.search).get('clientSlug') ?? 'test';
    const hash = await hashBody(input.body);
    const id = (await convex.mutation(api.vaultOutboundQueue.enqueue as never, {
      clientSlug: slug,
      notePath: input.notePath,
      bodyHash: hash,
      bodyPreview: bodyPreview(input.body),
    } as never)) as string;
    setPendingWriteBody(id, input.body);
    return id;
  };

  /**
   * Drain the outbound queue. Uses a no-op saveBack (marks rows
   * completed without real Graph call) unless tests have installed
   * a saveBack override via window.__repancyTestInstallSaveBack
   * (future hook — not in this commit).
   */
  w.__repancyTestDrainOutbound = async () => {
    const convex = requireConvex();
    const api = loadConvexApi();
    if (!api) throw new Error('__repancyConvexApi not registered');
    const slug =
      new URLSearchParams(window.location.search).get('clientSlug') ?? 'test';
    const { drainOutbound } = await import('../graph/syncDriveOutbound');
    await drainOutbound(
      convex,
      { vaultOutboundQueue: api.vaultOutboundQueue },
      injectedSaveBackForTests, // null → synthetic completion; injected → test path
      (qid) => getPendingWriteBody(qid),
      { clientSlug: slug },
      { maxPerCycle: 20 },
    );
  };

  // F-OUTBOUND-SAVEBACK-HOOK installer. Stores the fn (or null to clear)
  // in module scope. Drain hook reads it on every call.
  w.__repancyTestInstallSaveBack = (fn) => {
    injectedSaveBackForTests = fn;
  };

  // F-FAKE-DIR-PICKER — replaces window.showDirectoryPicker for the
  // J vault-export-folder spec. Clears any prior write list so each
  // test starts fresh.
  w.__repancyTestInstallFakeDirectoryPicker = () => {
    resetFakeWrites();
    installFakeDirectoryPicker();
  };
  w.__repancyTestGetFakeWrites = () => getFakeWrites();

  /** Read the status of an outbound-queue row. */
  w.__repancyTestGetOutboundStatus = async (id) => {
    const convex = requireConvex();
    const api = loadConvexApi();
    if (!api) throw new Error('__repancyConvexApi not registered');
    const slug =
      new URLSearchParams(window.location.search).get('clientSlug') ?? 'test';
    const rows = (await convex.query(api.vaultOutboundQueue.listForSlug as never, {
      clientSlug: slug,
    } as never)) as Array<{ _id: string; status: string }>;
    return rows.find((r) => r._id === id)?.status ?? 'unknown';
  };

  // Diagnostics ping so specs can confirm the hooks installed.
  (
    w as TestHookWindow & {
      __repancyTestHooksInstalled?: { at: string; version: number };
    }
  ).__repancyTestHooksInstalled = {
    at: new Date().toISOString(),
    version: 1,
  };

  // Re-export pendingWriteBodies surface so specs can inspect the
  // in-memory body cache + auto-approve attribution without importing
  // from src/. Bundle impact in prod: tree-shaken because
  // shouldInstall() returns false and these references die.
  (
    w as TestHookWindow & {
      __repancyPendingWriteBodies?: {
        setPendingWriteBody: typeof setPendingWriteBody;
        getPendingWriteBody: typeof getPendingWriteBody;
        clearPendingWriteBody: typeof clearPendingWriteBody;
        hashBody: typeof hashBody;
        setAutoApproveAttribution: typeof setAutoApproveAttribution;
        getAutoApproveAttribution: typeof getAutoApproveAttribution;
        listCachedPendingWriteIds: typeof listCachedPendingWriteIds;
        bodyPreview: typeof bodyPreview;
      };
    }
  ).__repancyPendingWriteBodies = {
    setPendingWriteBody,
    getPendingWriteBody,
    clearPendingWriteBody,
    hashBody,
    setAutoApproveAttribution,
    getAutoApproveAttribution,
    listCachedPendingWriteIds,
    bodyPreview,
  };
}

/**
 * Clear all test-injected state. Useful for spec teardown.
 * Self-gated — no-op outside test mode.
 */
export function resetTestHooks(): void {
  if (!shouldInstall()) return;
  testPendingWrites.length = 0;
  notifyTestPendingListeners();
  injectedSaveBackForTests = null;
  resetFakeWrites();
  uninstallFakeDirectoryPicker();
}
